import { useState, useRef, useCallback } from "react";
import { useAppAuth } from "@/hooks/useAppAuth";
import { X, Camera } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import Cropper from "react-easy-crop";
import api from "../../http";
import { useI18n } from "@/contexts/I18nContext";
import { appConfig } from "@/appConfig";
import { useColor } from "@/contexts/ColorContext";

interface ProfileImageModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentUser: {
        id: string;
        first_name?: string | null;
        last_name?: string | null;
        email: string;
        profile_image_url?: string | null;
    } | null;
    onProfileImageUpdate: (imageUrl: string) => void;
    onUserInfoUpdate: (updatedUser: {
        id: string;
        first_name?: string | null;
        last_name?: string | null;
        email: string;
        profile_image_url?: string | null;
    }) => void;
}

// Helper function to create image cropping
const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', error => reject(error));
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });

const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: { x: number; y: number; width: number; height: number }
): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        throw new Error('No 2d context');
    }

    // Set canvas size to crop size
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Draw the cropped image
    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    );

    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            if (blob) resolve(blob);
        }, 'image/jpeg', 0.9);
    });
};

const ProfileImageModal = ({
    isOpen,
    onClose,
    currentUser,
    onProfileImageUpdate,
}: ProfileImageModalProps) => {
    const { getAccessToken } = useAppAuth();
    const { toast } = useToast();
    const { t } = useI18n();
    const { themeColor } = useColor();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const APP_COLOR = appConfig.features.isDemoMode
        ? themeColor
        : appConfig.themeColor;

    // Form state
    const [isUploading, setIsUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);

    // Image cropping state
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
        x: number;
        y: number;
        width: number;
        height: number;
    } | null>(null);
    const [showCropper, setShowCropper] = useState(false);

    const onCropComplete = useCallback(
        (_croppedArea: any, croppedAreaPixels: any) => {
            setCroppedAreaPixels(croppedAreaPixels);
        },
        []
    );

    const handleFileSelect = (file: File) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast({
                title: t("profile.invalidTypeTitle"),
                description: t("profile.invalidTypeDescription"),
                variant: "destructive",
            });
            return;
        }

        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
            toast({
                title: t("profile.fileTooLargeTitle"),
                description: t("profile.fileTooLargeDescription"),
                variant: "destructive",
            });
            return;
        }

        // Create image URL for cropper
        const imageUrl = URL.createObjectURL(file);
        setImageSrc(imageUrl);
        setShowCropper(true);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
    };

    const handleCropSave = async () => {
        if (!imageSrc || !croppedAreaPixels) return;

        try {
            setIsUploading(true);

            // Get cropped image as blob
            const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);

            // Convert blob to file
            const croppedFile = new File([croppedImageBlob], 'profile-image.jpg', {
                type: 'image/jpeg'
            });

            // Upload cropped image
            const token = await getAccessToken();
            const response = await api.uploadProfileImage(croppedFile, token);

            onProfileImageUpdate(response.profile_image_url);

            toast({
                title: t("profile.imageUpdated"),
                description: t("profile.imageUpdatedDescription"),
            });

            // Reset cropper state
            setShowCropper(false);
            setImageSrc(null);
            URL.revokeObjectURL(imageSrc);
        } catch (error) {
            console.error("Failed to upload profile image:", error);
            toast({
                title: t("profile.uploadFailed"),
                description: t("profile.uploadFailedDescription"),
                variant: "destructive",
            });
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleCropCancel = () => {
        if (imageSrc) {
            URL.revokeObjectURL(imageSrc);
        }
        setShowCropper(false);
        setImageSrc(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const getInitials = () => {
        if (!currentUser) return "U";
        const firstName = currentUser.first_name || "";
        const lastName = currentUser.last_name || "";
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "U";
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md relative">
                {showCropper ? (
                    // Image Cropper View
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold">{t("profile.cropImage")}</h2>
                            <button
                                onClick={handleCropCancel}
                                className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-black hover:text-gray-700 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="relative w-full h-64 bg-gray-100 rounded-lg overflow-hidden mb-4">
                            <Cropper
                                image={imageSrc!}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                cropShape="round"
                                showGrid={false}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Zoom
                            </label>
                            <input
                                type="range"
                                min={1}
                                max={3}
                                step={0.1}
                                value={zoom}
                                onChange={(e) => setZoom(Number(e.target.value))}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            />
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={handleCropCancel}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-md bg-transparent hover:bg-slate-100 transition-colors"
                            >
                                {t("common.cancel")}
                            </button>
                            <button
                                onClick={handleCropSave}
                                disabled={isUploading}
                                className="flex-1 rounded-md border px-4 py-2 text-white transition-colors disabled:opacity-50"
                                style={{
                                    backgroundColor: APP_COLOR,
                                    borderColor: APP_COLOR,
                                }}
                            >
                                {isUploading ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        {t("common.loading")}
                                    </div>
                                ) : (
                                    t("profile.uploadImage")
                                )}
                            </button>
                        </div>
                    </div>
                ) : (
                    // Profile Edit View
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold">{t("common.profile")}</h2>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-black hover:text-gray-700 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="flex justify-center mb-4">
                            <div
                                className={`relative group cursor-pointer ${dragActive ? 'ring-2 ring-blue-400' : ''
                                    }`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <Avatar className="w-20 h-20 group-hover:ring-2 group-hover:ring-blue-400 transition-all">
                                    <AvatarImage src={currentUser?.profile_image_url || undefined} alt="Profile" />
                                    <AvatarFallback className="text-lg font-medium">
                                        {getInitials()}
                                    </AvatarFallback>
                                </Avatar>

                                {/* Upload Overlay */}
                                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Camera className="w-6 h-6 text-white" />
                                </div>

                                {/* Loading Overlay */}
                                {isUploading && (
                                    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <p className="text-xs text-gray-500 text-center mb-4">
                            {t("settings.clickPhoto")}
                        </p>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileInput}
                            className="hidden"
                        />

                        <div className="space-y-3 rounded-md border border-gray-200 bg-slate-50 p-4">
                            <div>
                                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                    {t("common.profile")}
                                </div>
                                <div className="mt-1 text-sm font-medium text-gray-900">
                                    {currentUser?.first_name && currentUser?.last_name
                                        ? `${currentUser.first_name} ${currentUser.last_name}`
                                        : currentUser?.email}
                                </div>
                            </div>
                            {currentUser?.email && (
                                <div className="text-xs text-gray-500">
                                    {currentUser.email}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={onClose}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md bg-transparent hover:bg-slate-100 transition-colors"
                            >
                                {t("commentModal.close")}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProfileImageModal; 
